import { useState } from 'react';
import {
  useAllDdQuestions,
  useAnswerPhaseQuestion,
  useCreatePhaseQuestion,
  useDdAccounts,
  useDdStates,
  useDeletePhaseQuestion,
  useUpdatePhaseQuestion,
} from '@/api/hooks';
import {
  ApiError,
  Button,
  ConfirmDialog,
  Icon,
  RowMenu,
  cn,
  readableRosterPrompt,
} from '@dataroom/shared';
import type { DdPhaseQuestion, DdPhaseSummary } from '@dataroom/shared';
import { DdAccountsPanel } from './DdAccountsPanel';
import { DdStatesPanel } from './DdStatesPanel';

/**
 * QUESTIONS FOR THE SELLER (redesign slice 6): every phase's free-text questions
 * and the account and state rosters in one panel, directly under the phase plan,
 * with how many are still unanswered — instead of a box at the very foot of each
 * phase, after hundreds of items. The rosters drive the per-account and per-state
 * categories, so they are worth finding. (Not to be confused with the Q&A tab's
 * seller questions, a different feature.)
 */
export function PhaseQuestionsPanel({
  roomId,
  phases,
}: {
  roomId: string;
  /** The room's real phases, in order. */
  phases: DdPhaseSummary[];
}) {
  const questions = useAllDdQuestions(roomId);
  const accounts = useDdAccounts(roomId);
  const states = useDdStates(roomId);
  const [open, setOpen] = useState(false);
  const list = questions.data ?? [];

  /** A roster is answered once it lists something; a question once it has an answer. */
  const answered = (q: DdPhaseQuestion): boolean => {
    if (q.stateRoster) return (states.data?.states.length ?? 0) > 0;
    if (q.rosterKind) return (accounts.data?.accounts ?? []).some((a) => a.kind === q.rosterKind);
    return Boolean(q.answer);
  };
  const unanswered = list.filter((q) => !answered(q)).length;
  const summary = questions.isPending
    ? 'Loading…'
    : list.length === 0
      ? 'None yet'
      : unanswered === 0
        ? 'All answered'
        : `${unanswered} unanswered`;

  return (
    <section aria-labelledby="seller-questions-h" className="rounded-lg border">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3">
        <h3 id="seller-questions-h" className="text-base font-semibold">
          <button
            type="button"
            aria-expanded={open}
            aria-controls="seller-questions-body"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 rounded-md text-left hover:underline"
          >
            <Icon
              name="chevronRight"
              className={cn('text-ink-3 transition-transform', open && 'rotate-90')}
            />
            Questions for the seller
          </button>
        </h3>
        <span
          className={cn(
            'text-xs',
            unanswered > 0 ? 'font-medium text-brass' : 'text-ink-3',
          )}
        >
          {summary}
        </span>
        <span className="basis-full text-xs text-ink-3 md:ml-auto md:basis-auto">
          Questions the seller answers in a form, and the lists of accounts and states.
        </span>
      </div>
      {open ? (
        <div id="seller-questions-body" className="space-y-4 border-t px-4 pb-4 pt-3">
          {questions.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : questions.error instanceof ApiError ? (
            <p className="text-sm text-destructive">{questions.error.message}</p>
          ) : (
            phases.map((phase) => (
              <PhaseQuestions
                key={phase.phaseId}
                roomId={roomId}
                phase={phase}
                questions={list.filter((q) => q.phaseId === phase.phaseId)}
              />
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}

/** One phase's questions in the panel: each question, then "Add a question…". */
function PhaseQuestions({
  roomId,
  phase,
  questions,
}: {
  roomId: string;
  phase: DdPhaseSummary;
  questions: DdPhaseQuestion[];
}) {
  const createQuestion = useCreatePhaseQuestion(roomId);
  const [prompt, setPrompt] = useState('');
  const phaseId = phase.phaseId!;
  const createError =
    createQuestion.error instanceof ApiError ? createQuestion.error.message : null;

  function submit() {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    createQuestion.mutate(
      { phaseId, body: { prompt: trimmed } },
      { onSuccess: () => setPrompt('') },
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">{phase.name}</h4>
      {questions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No questions in this phase yet.</p>
      ) : (
        <ul className="space-y-2">
          {questions.map((q) => (
            <QuestionRow key={q.id} roomId={roomId} question={q} />
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          aria-label={`Add a question to ${phase.name}`}
          value={prompt}
          disabled={createQuestion.isPending}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder="Add a question…"
          className="min-w-0 flex-1 rounded border border-input bg-background px-2 py-1 text-sm"
        />
        <Button size="sm" disabled={createQuestion.isPending || !prompt.trim()} onClick={submit}>
          {createQuestion.isPending ? 'Adding…' : 'Add question'}
        </Button>
      </div>
      {createError ? <p className="text-xs text-destructive">{createError}</p> : null}
    </div>
  );
}

/** One question row: the prompt (editable inline), a delete control, the seller's
 * answer (read-only display), and an admin override/clear of the answer. */
function QuestionRow({ roomId, question }: { roomId: string; question: DdPhaseQuestion }) {
  const updateQuestion = useUpdatePhaseQuestion(roomId);
  const deleteQuestion = useDeletePhaseQuestion(roomId);
  const answerQuestion = useAnswerPhaseQuestion(roomId);

  const [editing, setEditing] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState(question.prompt);
  const [overriding, setOverriding] = useState(false);
  const [draftAnswer, setDraftAnswer] = useState(question.answer ?? '');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const actionError =
    updateQuestion.error instanceof ApiError
      ? updateQuestion.error.message
      : deleteQuestion.error instanceof ApiError
        ? deleteQuestion.error.message
        : answerQuestion.error instanceof ApiError
          ? answerQuestion.error.message
          : null;

  return (
    <li className="rounded-md border border-border p-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={draftPrompt}
                disabled={updateQuestion.isPending}
                onChange={(e) => setDraftPrompt(e.target.value)}
                className="min-w-0 flex-1 rounded border border-input bg-background px-2 py-1 text-sm"
              />
              <Button
                size="sm"
                disabled={updateQuestion.isPending || !draftPrompt.trim()}
                onClick={() =>
                  updateQuestion.mutate(
                    { questionId: question.id, body: { prompt: draftPrompt.trim() } },
                    { onSuccess: () => setEditing(false) },
                  )
                }
              >
                {updateQuestion.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={updateQuestion.isPending}
                onClick={() => {
                  setDraftPrompt(question.prompt);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <p className="text-sm font-medium">
              {question.rosterKind || question.stateRoster
                ? readableRosterPrompt(question.prompt)
                : question.prompt}
            </p>
          )}
        </div>
        {!editing ? (
          <RowMenu
            label={question.prompt}
            items={[
              {
                label: 'Edit question',
                icon: 'pen',
                onSelect: () => {
                  setDraftPrompt(question.prompt);
                  setEditing(true);
                },
              },
              {
                label: 'Remove',
                icon: 'trash',
                danger: true,
                onSelect: () => setConfirmRemove(true),
              },
            ]}
          />
        ) : null}
        {confirmRemove ? (
          <ConfirmDialog
            danger
            title="Remove this question?"
            confirmLabel="Remove question"
            pending={deleteQuestion.isPending}
            onCancel={() => setConfirmRemove(false)}
            onConfirm={() =>
              deleteQuestion.mutate(question.id, { onSettled: () => setConfirmRemove(false) })
            }
          >
            <p>
              “{question.prompt}” and any answer the seller gave are removed. This can’t be undone.
            </p>
          </ConfirmDialog>
        ) : null}
      </div>

      {question.stateRoster ? (
        // The STATE roster (issue #157): answered by choosing states — each gets
        // its own categories with that state's real filings — not by free text.
        <div className="mt-2">
          <DdStatesPanel roomId={roomId} />
        </div>
      ) : question.rosterKind ? (
        // A ROSTER question (ADR-0037) is answered by the account list — each
        // account becomes its own checklist category — not by free text.
        <div className="mt-2">
          <DdAccountsPanel roomId={roomId} kind={question.rosterKind} />
        </div>
      ) : (
        <>
          <div className="mt-1.5">
            {question.answer ? (
              <p className="whitespace-pre-wrap rounded bg-muted px-2 py-1 text-sm text-foreground">
                {question.answer}
              </p>
            ) : (
              <p className="text-xs italic text-muted-foreground">
                Not yet answered by the seller.
              </p>
            )}
          </div>

          {overriding ? (
            <div className="mt-1.5 space-y-1.5">
              <textarea
                value={draftAnswer}
                disabled={answerQuestion.isPending}
                onChange={(e) => setDraftAnswer(e.target.value)}
                rows={3}
                className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                placeholder="Override the answer (leave empty to clear)…"
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={answerQuestion.isPending}
                  onClick={() =>
                    answerQuestion.mutate(
                      { questionId: question.id, body: { answer: draftAnswer } },
                      { onSuccess: () => setOverriding(false) },
                    )
                  }
                >
                  {answerQuestion.isPending ? 'Saving…' : 'Save answer'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={answerQuestion.isPending}
                  onClick={() => {
                    setDraftAnswer(question.answer ?? '');
                    setOverriding(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraftAnswer(question.answer ?? '');
                setOverriding(true);
              }}
              className="mt-1 text-xs font-medium text-primary hover:underline"
            >
              {question.answer ? 'Override answer' : 'Answer on behalf of seller'}
            </button>
          )}
        </>
      )}

      {actionError ? <p className="mt-1 text-xs text-destructive">{actionError}</p> : null}
    </li>
  );
}

